'use client';

import { ReactNode, useCallback, useEffect, useRef, useState, type JSX } from 'react';
import dynamic from 'next/dynamic';
import NullState from '@alltrails/amplitude/enums/NullState';
import NullStateAction from '@alltrails/amplitude/enums/NullStateAction';
import ReportLocation from '@alltrails/amplitude/enums/ReportLocation';
import ReviewFilterAction from '@alltrails/amplitude/enums/ReviewFilterAction';
import ReviewShowMoreSection from '@alltrails/amplitude/enums/ReviewShowMoreSection';
import { FormattedMessage } from 'react-intl';
import type { LightboxPhoto, LightboxUser } from '@alltrails/denali/types';
import type ReviewPhoto from '@alltrails/shared/types/ReviewPhoto';
import type User from '@alltrails/core/types/User';
import Typography from '@alltrails/core/components/Typography';
import type { ReviewEntityType } from '@alltrails/redux/slices/reviewsSearchFilters';
import type TrailId from '@alltrails/core/types/TrailId';
import type Review from '@alltrails/core/types/Review';
import useUser from '@alltrails/context/hooks/useUser';
import { useDispatch, useSelector } from '../../redux';
import ReviewsSearchBox from '../SearchFilter/ReviewsSearchBox';
import NoResults from '../SearchFilter/NoResults/NoResults';
import ReviewsBase from '../ReviewsBase/ReviewsBase';
import SearchLoading from '../SearchFilter/SearchLoading/SearchLoading';
import SearchError from '../SearchFilter/SearchError/SearchError';
import ReviewsSearchCount from '../SearchFilter/ReviewsSearchCount/ReviewsSearchCount';
import getReviewFilterCount from '../../utils/getReviewFilterCount';
import { reviewsApi, useSearchReviewsQuery, ReviewsApiResponse } from '../../slices/reviewsApi';
import { updateSummaryExclusion } from '../../utils/reviewSummaryRequests';
import type { SortOption } from '../../types/Reviews';
import isUserDiAdmin from './isUserDiAdmin';
import styles from './SearchFilterReviews.module.scss';

const FilterModal = dynamic(() => import('../SearchFilter/FilterModal'), { ssr: false });

export type ResultsReturnedParams = {
  action?: ReviewFilterAction;
  activityTypes?: string[];
  entityType?: ReviewEntityType;
  months?: number[];
  query?: string;
  ratings?: number[];
};

type SearchFilterReviewsProps = {
  ads?: ReactNode[];
  feedbackKey?: string;
  hideReviewSummary?: boolean;
  initialReviews: ReviewsApiResponse;
  onActivityLinkClick: (review: Review) => void;
  onDeleteReview?: (review: Review) => void;
  onFilterClick?: () => void;
  onFilterShowMoreClick?: (section: ReviewShowMoreSection) => void;
  onLightboxClose?: () => void;
  onLightboxPhotoViewed?: (review: Review, photo: LightboxPhoto['photo']) => void;
  onLightboxUserClick?: (user: LightboxUser) => void;
  onLoadMore: () => boolean;
  onNullStateCtaClick?: (action: NullStateAction, state: NullState) => void;
  onNullStateViewed?: (state: NullState) => void;
  onResultsReturned?: (params: ResultsReturnedParams) => void;
  onReviewPhotoClick?: (review: Review, photo: ReviewPhoto) => void;
  onReviewPhotosScroll?: (review: Review, numPhotos: number) => void;
  onReviewPhotosScrollEnd?: (review: Review, numPhotos: number) => void;
  onSearchFocus?: () => void;
  onSortChange?: (oldSort: SortOption, newSort: SortOption) => void;
  onToggleSummaryExclusion?: (review: Review, summaryExclusion: boolean) => void;
  onUpdateReview?: (data: { detail: Review }) => void;
  onUserClick?: (user: User) => void;
  openReviewFormModal?: (trailFormProps: any) => void;
  perPage?: number;
  photosClassName?: string;
  reportLocation?: ReportLocation;
  reviewSummary?: string;
  searchBoxClassName?: string;
  total: number;
  trailId: TrailId;
  photosOverflowOnMobile?: boolean;
  helpfulReviews?: Review[];
};

const SearchFilterReviews = ({
  ads,
  initialReviews,
  onActivityLinkClick,
  onDeleteReview,
  onFilterClick,
  onFilterShowMoreClick,
  onLightboxClose,
  onLightboxPhotoViewed,
  onLightboxUserClick,
  onLoadMore,
  onNullStateCtaClick,
  onNullStateViewed,
  onResultsReturned,
  onReviewPhotoClick,
  onReviewPhotosScroll,
  onReviewPhotosScrollEnd,
  onSearchFocus,
  onToggleSummaryExclusion,
  onUpdateReview,
  onUserClick,
  openReviewFormModal,
  perPage = 6,
  photosClassName,
  reportLocation,
  searchBoxClassName,
  total,
  trailId,
  photosOverflowOnMobile,
  helpfulReviews
}: SearchFilterReviewsProps): JSX.Element => {
  const currentUser = useUser();
  const containerRef = useRef<HTMLDivElement>(null);
  const reviewsRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const context = useSelector(state => state.context);
  const { query, ratings, activities, months } = useSelector(state => state.reviewsSearchFilters.permanent);

  const [cursor, setCursor] = useState<string | undefined>();
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [renderCachedReviews, setRenderCachedReviews] = useState(false);
  const [searchAction, setSearchAction] = useState<ReviewFilterAction | undefined>();

  const { data, isFetching, isError } = useSearchReviewsQuery(
    {
      after: cursor,
      limit: perPage,
      rating: ratings,
      activity: activities,
      month: months,
      query,
      trailId
    },
    { skip: !renderCachedReviews }
  );

  const onToggleSummaryExclusionInternal = useCallback(
    (review: Review, summaryExclusion: boolean) => {
      onToggleSummaryExclusion?.(review, summaryExclusion);
      updateSummaryExclusion(review, summaryExclusion).catch((e: Error) => {
        console.error(`Failed to update summary exclusion: ${e?.message}`);
      });
    },
    [onToggleSummaryExclusion]
  );

  const onFilterClickInternal = useCallback(() => {
    onFilterClick?.();
    setFilterModalOpen(true);
  }, [onFilterClick]);

  const resetPage = useCallback(() => setCursor(undefined), []);

  const onSearch = useCallback((action?: ReviewFilterAction) => {
    resetPage();
    setSearchAction(action);
    setTimeout(() => {
      containerRef.current?.focus({ preventScroll: true });
      containerRef.current?.scrollIntoView();
    }, 0);
  }, []);

  const onNullStateCtaClickInternal = useCallback(
    (action: NullStateAction, state: NullState) => {
      onNullStateCtaClick?.(action, state);
      containerRef.current?.focus({ preventScroll: true });
      containerRef.current?.scrollIntoView();
    },
    [onNullStateCtaClick]
  );

  const onNoResultsClear = useCallback(
    (action: NullStateAction, state: NullState) => {
      onNullStateCtaClickInternal?.(action, state);
      resetPage();
    },
    [onNullStateCtaClick]
  );

  const onLoadMoreInternal = useCallback(() => {
    if (onLoadMore()) {
      setCursor(data?.pageInfo.nextCursor);
    }
  }, [data?.pageInfo.nextCursor, onLoadMore]);

  useEffect(() => {
    if (!data?.trail_reviews.length || isFetching || isError || !searchAction) return;
    onResultsReturned?.({
      action: searchAction,
      activityTypes: activities,
      entityType: 'reviews',
      months,
      query,
      ratings
    });
  }, [data?.trail_reviews, isError, isFetching, searchAction]);

  /**
   * HACK: Initialize the reviews cache with the initial set of reviews passed from props.
   * See https://redux-toolkit.js.org/rtk-query/usage/server-side-rendering for ideal SSR setup.
   * With the introduction of RSC's however, there is no formal way to initialize the cache inside server components.
   */
  const initializeReviewsCache = async () => {
    await dispatch(
      reviewsApi.util.upsertQueryData(
        'searchReviews',
        {
          after: cursor,
          limit: perPage,
          rating: ratings,
          activity: activities,
          month: months,
          query,
          trailId
        },
        initialReviews
      )
    );

    setRenderCachedReviews(true);
  };

  useEffect(() => {
    if (initialReviews?.trail_reviews.length) {
      initializeReviewsCache();
    } else {
      setRenderCachedReviews(true);
    }
    // only run this once to avoid unwanted manual cache updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (total === 0) {
    return <></>;
  }

  const hasFilters = getReviewFilterCount('reviews', ratings, activities, months) > 0;
  const hasSearch = !!query?.length;
  const canLoadMore = data?.pageInfo.hasNextPage ?? !!(data?.trail_reviews?.length && data.trail_reviews.length);
  const renderCount = data?.exclusiveMaximum ?? data?.meta.items ?? data?.pageInfo?.itemCount ?? data?.trail_reviews?.length ?? perPage;

  const reviews = data?.trail_reviews ? data.trail_reviews : initialReviews.trail_reviews;
  let displayReviews = reviews;
  if (helpfulReviews?.length && !isUserDiAdmin(currentUser?.id) && !hasFilters && !hasSearch) {
    const helpfulReviewsIds = helpfulReviews.map(review => review.id);
    const reviewsWithoutHelpfulReviews = reviews.filter(review => !helpfulReviewsIds.includes(review.id));
    displayReviews = helpfulReviews.concat(reviewsWithoutHelpfulReviews);
  }

  return (
    <div ref={containerRef} className={styles.container} tabIndex={-1}>
      <ReviewsSearchBox className={searchBoxClassName} onFilterClick={onFilterClickInternal} onSearch={onSearch} onSearchFocus={onSearchFocus} />
      {!isError && (!(isFetching && hasSearch) || !!cursor) && (hasFilters || hasSearch) && renderCount > 0 && (
        <ReviewsSearchCount count={renderCount} hasFilters={hasFilters} hasSearch={hasSearch} query={query} />
      )}
      {isFetching && !cursor && <SearchLoading repeatNum={6} />}
      {isError && <SearchError onCtaClick={onNullStateCtaClickInternal} onViewed={onNullStateViewed} trailId={trailId} />}
      {(!isFetching || !!cursor) && !isError && (
        <ReviewsBase
          ref={reviewsRef}
          ads={ads}
          canLoadMore={canLoadMore}
          context={context}
          emptyState={
            total === 0 ? (
              <div className={styles.emptyReviews}>
                <FormattedMessage defaultMessage="No one has reviewed this trail yet. Be the first!" />
              </div>
            ) : (
              <NoResults hasFilters={hasFilters} hasSearch={hasSearch} onClear={onNoResultsClear} onViewed={onNullStateViewed} />
            )
          }
          hideSort /* ReviewGPT is rendered after the sort, so hide it by default and render our own */
          isLoadingMore={isFetching && !!cursor}
          localizeOnLoad
          onActivityLinkClick={onActivityLinkClick}
          onDeleteReview={onDeleteReview}
          onLightboxClose={onLightboxClose}
          onLightboxPhotoViewed={onLightboxPhotoViewed}
          onLightboxUserClick={onLightboxUserClick}
          onLoadMore={onLoadMoreInternal}
          onReviewPhotoClick={onReviewPhotoClick}
          onReviewPhotosScroll={onReviewPhotosScroll}
          onReviewPhotosScrollEnd={onReviewPhotosScrollEnd}
          onToggleSummaryExclusion={onToggleSummaryExclusionInternal}
          onUpdateReview={onUpdateReview}
          onUserClick={onUserClick}
          openReviewFormModal={openReviewFormModal}
          preferHTML
          photosClassName={photosClassName}
          reportLocation={reportLocation}
          resultsFooter={
            total !== 0 &&
            !isError &&
            (hasFilters || hasSearch) &&
            renderCount > 0 && (
              <Typography className={styles.localeMessaging} color="secondary" component="div" variant="text100">
                <FormattedMessage defaultMessage="Search results don’t include reviews that have been translated from another language." />
              </Typography>
            )
          }
          reviews={displayReviews}
          total={total}
          photosOverflowOnMobile={photosOverflowOnMobile}
        />
      )}
      {filterModalOpen && (
        <FilterModal
          initialReviewsCount={total}
          isOpen={filterModalOpen}
          onClose={() => setFilterModalOpen(false)}
          onFilterSave={onSearch}
          onShowMoreClick={onFilterShowMoreClick}
          perPage={perPage}
          trailId={trailId}
        />
      )}
    </div>
  );
};
export default SearchFilterReviews;
